import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "../../../contexts/LanguageContext";
import SectionHeading from "../SectionHeading";

const FEATURE_KEYS = [
  "appFeature1",
  "appFeature2",
  "appFeature3",
  "appFeature4",
];

/** Photos distinctes de Hero/TopList/NewDish/ScrollGallery */
const COLLAGE_PHOTOS = {
  main: "https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=900&h=1100&fit=crop&q=85",
  secondary: "https://images.unsplash.com/photo-1541014741259-de529411b96a?w=700&h=700&fit=crop&q=85",
  accent: "https://images.unsplash.com/photo-1512152272829-e3139592d56f?w=500&h=500&fit=crop&q=85",
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
  },
};

const CheckIcon = () => (
  <svg
    className="w-5 h-5 text-ember-600 shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const photoVariants = {
  hidden: { opacity: 0, scale: 0.92, y: 16 },
  visible: (delay = 0) => ({
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
  }),
};

/**
 * Section « JesCook sur le web » : accès partout via le navigateur, sans
 * application à installer. Collage photo (même famille que le Hero, en
 * miroir et accent ember plutôt que pine) plutôt qu'un mock de navigateur
 * en thème sombre, qui tranchait avec le reste de la page en clair.
 */
const AppDownloadSection = ({ onViewRecipes }) => {
  const { t } = useLanguage();
  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 w-full min-w-0">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-16 items-center">
        {/* Colonne texte */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={containerVariants}
        >
          <SectionHeading
            index="04"
            kicker={t("appAccessLabel")}
            title={t("appWebTitle")}
            description={t("appWebDesc")}
            className="mb-8"
          />

          <motion.ul className="space-y-3 mb-9" variants={containerVariants}>
            {FEATURE_KEYS.map((key) => (
              <motion.li
                key={key}
                variants={itemVariants}
                className="flex items-start gap-3 text-ink-700 text-sm"
              >
                <CheckIcon />
                <span>{t(key)}</span>
              </motion.li>
            ))}
          </motion.ul>

          <motion.div variants={itemVariants}>
            {onViewRecipes ? (
              <button
                type="button"
                onClick={onViewRecipes}
                className="inline-flex items-center gap-2 px-6 py-3 bg-ember-500 text-ink-950 text-sm font-semibold rounded-lg hover:bg-ember-400 transition-colors duration-150"
              >
                {t("discoverRecipesButton")}
              </button>
            ) : (
              <Link
                to="/recipes"
                className="inline-flex items-center gap-2 px-6 py-3 bg-ember-500 text-ink-950 text-sm font-semibold rounded-lg hover:bg-ember-400 transition-colors duration-150"
              >
                {t("discoverRecipesButton")}
              </Link>
            )}
          </motion.div>
        </motion.div>

        {/* Illustration : collage photo, même famille que le Hero (en miroir, accent ember) */}
        <div className="relative flex items-center justify-center w-full max-w-[360px] sm:max-w-[440px] lg:max-w-none mx-auto min-h-[340px] sm:min-h-[420px] lg:min-h-[460px]">
          {/* Photo principale */}
          <motion.div
            className="relative w-[220px] h-[280px] sm:w-[270px] sm:h-[340px] lg:w-[300px] lg:h-[380px] rounded-[28px] overflow-hidden shadow-xl shadow-ink-900/15 z-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            custom={0.1}
            variants={photoVariants}
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <img
              src={COLLAGE_PHOTOS.main}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </motion.div>

          {/* Photo satellite, cadrée ember (miroir du cadre pine du Hero) */}
          <motion.div
            className="absolute left-0 bottom-4 sm:bottom-6 w-[140px] h-[140px] sm:w-[170px] sm:h-[170px] rounded-3xl bg-ember-600 p-2 shadow-xl shadow-ink-900/15 z-20"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            custom={0.28}
            variants={photoVariants}
            whileHover={{ scale: 1.04, rotate: 2 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="w-full h-full rounded-2xl overflow-hidden">
              <img
                src={COLLAGE_PHOTOS.secondary}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </motion.div>

          {/* Petit accent photo, en haut à droite (miroir du Hero) */}
          <motion.div
            className="absolute right-0 top-6 sm:top-8 w-[92px] h-[92px] sm:w-[104px] sm:h-[104px] rounded-2xl overflow-hidden border-4 border-ink-50 shadow-lg shadow-ink-900/15 z-20 rotate-[6deg]"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            custom={0.42}
            variants={photoVariants}
            whileHover={{ scale: 1.06, rotate: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <img
              src={COLLAGE_PHOTOS.accent}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </motion.div>

          {/* Badge "Aucune app" */}
          <motion.div
            className="absolute bottom-0 left-2 sm:left-4 bg-ember-500 text-ink-950 text-xs sm:text-sm font-semibold px-4 py-2 rounded-full shadow-lg z-30"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            custom={0.52}
            variants={photoVariants}
          >
            {t("noDownload")}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AppDownloadSection;
